/**
 * <s-product-gallery> — one-at-a-time product media gallery
 * Shows a single slide in a scroll-snap track, with prev/next arrows and
 * thumbnail buttons. Swiping the track keeps the active thumbnail in sync.
 *
 * Markup contract:
 *   [data-gallery-track]  scroll-snap container (position: relative)
 *   [data-gallery-slide]  one per media item
 *   [data-gallery-thumbs] thumbnail strip
 *   [data-gallery-thumb]  thumbnail button with data-index
 *
 * Commands (via commandfor="<gallery id>"):
 *   --prev, --next, --go (reads event.source.dataset.index)
 */
class SProductGallery extends HTMLElement {
  #ac;
  #observer;
  #track;
  #slides = [];
  #thumbs = [];
  #thumbStrip;
  #index = 0;

  connectedCallback() {
    this.#track = this.querySelector('[data-gallery-track]');
    if (!this.#track) return;

    this.#slides = [...this.#track.querySelectorAll('[data-gallery-slide]')];
    this.#thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.#thumbStrip = this.querySelector('[data-gallery-thumbs]');

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', (event) => {
      if (event.command === '--prev') {
        this.goTo(this.#index - 1);
      } else if (event.command === '--next') {
        this.goTo(this.#index + 1);
      } else if (event.command === '--go') {
        const target = Number(event.source?.dataset.index);
        if (!Number.isNaN(target)) this.goTo(target);
      }
    }, opts);

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) this.#setActive(this.#slides.indexOf(entry.target));
      }
    }, { root: this.#track, threshold: 0.6 });

    for (const slide of this.#slides) this.#observer.observe(slide);

    this.#setActive(0);

    // Opened with ?variant=…: start on that variant's photo (marker rendered in the product-form partial).
    if (new URLSearchParams(window.location.search).has('variant')) {
      const marker = document.querySelector('[data-variant-media-index]');
      const start = Number(marker?.dataset.variantMediaIndex);
      if (marker && !Number.isNaN(start) && this.#slides[start]) {
        requestAnimationFrame(() => {
          this.#track.scrollTo({ left: this.#slides[start].offsetLeft, behavior: 'auto' });
          this.#setActive(start);
        });
      }
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  goTo(index) {
    const count = this.#slides.length;
    if (count === 0) return;

    const target = ((index % count) + count) % count;
    const slide = this.#slides[target];
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#track.scrollTo({ left: slide.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
    this.#setActive(target);
  }

  #setActive(index) {
    if (index < 0) return;
    this.#index = index;

    this.#thumbs.forEach((thumb, thumbIndex) => {
      thumb.setAttribute('aria-current', thumbIndex === index ? 'true' : 'false');
    });

    const activeThumb = this.#thumbs[index];
    if (activeThumb && this.#thumbStrip) {
      const left = activeThumb.offsetLeft - (this.#thumbStrip.clientWidth - activeThumb.offsetWidth) / 2;
      this.#thumbStrip.scrollTo({ left, behavior: 'auto' });
    }
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
